import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import apiClient from '../../lib/api';
import { STAGES } from './constants';

const { FiX, FiExternalLink, FiTrash2 } = FiIcons;

// Full account view: restage, claim/release, log notes & status, read the activity
// feed. Everything writes through /api/crm and refreshes the parent board.
const AccountDetailModal = ({ accountId, currentUserId, usersById, onClose, onChanged }) => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteBody, setNoteBody] = useState('');
  const [noteKind, setNoteKind] = useState('note');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiClient.getCrmAccount(accountId);
      setAccount(data);
    } catch (err) {
      console.error('Failed to load account:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const ownerName = (id) => (id ? usersById[id]?.name || usersById[id]?.email || 'Unknown' : null);

  const refresh = async () => { await load(); onChanged?.(); };

  // Run a write action, surfacing any failure instead of silently swallowing it.
  const runAction = async (label, fn) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      console.error(`Failed to ${label}:`, err);
      setActionError(`Couldn't ${label}: ${err.message || 'request failed'}`);
    } finally {
      setBusy(false);
    }
  };

  const setStage = (stage) =>
    runAction(`change stage to ${stage}`, () => apiClient.updateCrmAccount(accountId, { stage }));

  const claim = () => runAction('claim account', () => apiClient.claimCrmAccount(accountId));

  const release = () => runAction('release account', () => apiClient.releaseCrmAccount(accountId));

  const addNote = async () => {
    if (!noteBody.trim()) return;
    await runAction('add note', async () => {
      await apiClient.addCrmNote(accountId, { body: noteBody.trim(), kind: noteKind });
      setNoteBody('');
    });
  };

  const remove = async () => {
    if (!window.confirm('Delete this account and all its notes?')) return;
    setBusy(true);
    setActionError(null);
    try { await apiClient.deleteCrmAccount(accountId); onChanged?.(); onClose(); }
    catch (err) {
      console.error('Failed to delete account:', err);
      setActionError(`Couldn't delete account: ${err.message || 'request failed'}`);
    }
    finally { setBusy(false); }
  };

  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString(); } catch { return d; }
  };

  const owned = account?.ownerUserId;
  const ownedByMe = owned && owned === currentUserId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {loading || !account ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-gray-100">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-gray-900">{account.company}</h2>
                  {account.coverageUrl && (
                    <a href={account.coverageUrl} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-600">
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
                  {account.amount && (
                    <span className="text-green-700 bg-green-50 rounded px-1.5 py-0.5 font-medium">
                      ${Number(account.amount).toLocaleString()}
                    </span>
                  )}
                  {account.series && <span className="text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">{account.series}</span>}
                  {account.leadInvestor && <span className="text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">Lead: {account.leadInvestor}</span>}
                  <span className="text-purple-700 bg-purple-50 rounded px-1.5 py-0.5">source: {account.source}</span>
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5 overflow-y-auto">
              {account.description && (
                <p className="text-sm text-gray-600">{account.description}</p>
              )}

              {actionError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-start justify-between gap-3">
                  <span>{actionError}</span>
                  <button
                    onClick={() => setActionError(null)}
                    className="text-red-500 hover:text-red-800 flex-shrink-0"
                    aria-label="Dismiss"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Stage + owner controls */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Stage</label>
                  <select
                    value={account.stage}
                    onChange={(e) => setStage(e.target.value)}
                    disabled={busy}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Owner</label>
                  <div className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-gray-800 truncate">
                      {ownerName(owned) || <span className="italic text-gray-400">Unclaimed</span>}
                    </span>
                    {owned ? (
                      <button onClick={release} disabled={busy} className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">
                        Release
                      </button>
                    ) : (
                      <button onClick={claim} disabled={busy} className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">
                        Claim
                      </button>
                    )}
                  </div>
                  {owned && !ownedByMe && (
                    <button onClick={claim} disabled={busy} className="text-xs text-blue-600 hover:underline mt-1">
                      Take over
                    </button>
                  )}
                </div>
              </div>

              {/* Add note / status */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Log an update</label>
                <textarea
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  rows={2}
                  placeholder="Add a note or status update…"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex items-center justify-between mt-2">
                  <select
                    value={noteKind}
                    onChange={(e) => setNoteKind(e.target.value)}
                    className="px-2 py-1 border border-gray-200 rounded text-xs"
                  >
                    <option value="note">Note</option>
                    <option value="status">Status</option>
                  </select>
                  <button
                    onClick={addNote}
                    disabled={busy || !noteBody.trim()}
                    className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Notes */}
              {account.notes?.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notes</h3>
                  <div className="space-y-2">
                    {account.notes.map((n) => (
                      <div key={n.id} className="text-sm bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          {n.kind === 'status' && (
                            <span className="text-xs text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">status</span>
                          )}
                          <span className="text-xs text-gray-400">
                            {ownerName(n.authorUserId) || 'System'} · {fmtDate(n.createdAt)}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{n.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity */}
              {account.activity?.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Activity</h3>
                  <ul className="space-y-1">
                    {account.activity.map((ev) => (
                      <li key={ev.id} className="text-xs text-gray-500 flex gap-2">
                        <span className="font-medium text-gray-700">{ev.verb}</span>
                        <span className="text-gray-400">{fmtDate(ev.createdAt)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center p-4 border-t border-gray-100">
              <button onClick={remove} disabled={busy} className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1">
                <SafeIcon icon={FiTrash2} className="w-4 h-4" /> Delete
              </button>
              <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm">Close</button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AccountDetailModal;
